import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function HomePage() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [showEnquiryModal, setShowEnquiryModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [demoFormData, setDemoFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [enquiryFormData, setEnquiryFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [demoSubmitting, setDemoSubmitting] = useState(false);
  const [enquirySubmitting, setEnquirySubmitting] = useState(false);
  const [demoSuccess, setDemoSuccess] = useState(false);
  const [enquirySuccess, setEnquirySuccess] = useState(false);
  const [demoError, setDemoError] = useState('');
  const [enquiryError, setEnquiryError] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const handleDemoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setDemoSubmitting(true);
    setDemoError('');

    // Basic validation
    if (!demoFormData.name.trim() || !demoFormData.email.trim() || !demoFormData.phone.trim()) {
      setDemoError('Please fill in all required fields');
      setDemoSubmitting(false);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(demoFormData.email)) {
      setDemoError('Please enter a valid email address');
      setDemoSubmitting(false);
      return;
    }

    const formDataToSend = new URLSearchParams();
    
    formDataToSend.append('name', demoFormData.name.trim());
    formDataToSend.append('email', demoFormData.email.trim());
    formDataToSend.append('phone', demoFormData.phone.trim());
    formDataToSend.append('message', demoFormData.message.trim() || 'No additional information provided');

    try {
      const response = await fetch('https://readdy.ai/api/form/d5ehn4b07s2llnhdb3hg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString()
      });

      if (response.ok) {
        setDemoSuccess(true);
        setDemoFormData({
          name: '',
          email: '',
          phone: '',
          message: ''
        });
        setTimeout(() => {
          setShowDemoModal(false);
          setDemoSuccess(false);
          navigate('/demo-video');
        }, 2000);
      } else {
        const errorText = await response.text();
        console.error('Form submission failed:', response.status, errorText);
        
        try {
          const errorJson = JSON.parse(errorText);
          if (errorJson.meta?.message === 'form data is spam') {
            setDemoError('Please wait a moment and try again. Make sure all fields are filled correctly.');
          } else {
            setDemoError('Unable to submit form. Please try again or contact us directly.');
          }
        } catch {
          setDemoError('Unable to submit form. Please try again or contact us directly.');
        }
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setDemoError('Network error. Please check your connection and try again.');
    } finally {
      setDemoSubmitting(false);
    }
  };

  const handleEnquirySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnquirySubmitting(true);
    setEnquiryError('');

    // Basic validation
    if (!enquiryFormData.name.trim() || !enquiryFormData.email.trim() || !enquiryFormData.phone.trim() || !enquiryFormData.subject.trim() || !enquiryFormData.message.trim()) {
      setEnquiryError('Please fill in all required fields');
      setEnquirySubmitting(false);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(enquiryFormData.email)) {
      setEnquiryError('Please enter a valid email address');
      setEnquirySubmitting(false);
      return;
    }

    const formDataToSend = new URLSearchParams();
    
    formDataToSend.append('name', enquiryFormData.name.trim());
    formDataToSend.append('email', enquiryFormData.email.trim());
    formDataToSend.append('phone', enquiryFormData.phone.trim());
    formDataToSend.append('subject', enquiryFormData.subject.trim());
    formDataToSend.append('message', enquiryFormData.message.trim());

    try {
      const response = await fetch('https://readdy.ai/api/form/d5ehn4b07s2llnhdb3i0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString()
      });

      if (response.ok) {
        setEnquirySuccess(true);
        setEnquiryFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
        setTimeout(() => {
          setShowEnquiryModal(false);
          setEnquirySuccess(false);
          navigate('/demo-video');
        }, 2000);
      } else {
        const errorText = await response.text();
        console.error('Form submission failed:', response.status, errorText);
        
        try {
          const errorJson = JSON.parse(errorText);
          if (errorJson.meta?.message === 'form data is spam') {
            setEnquiryError('Please wait a moment and try again. Make sure all fields are filled correctly.');
          } else {
            setEnquiryError('Unable to submit form. Please try again or contact us directly.');
          }
        } catch {
          setEnquiryError('Unable to submit form. Please try again or contact us directly.');
        }
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setEnquiryError('Network error. Please check your connection and try again.');
    } finally {
      setEnquirySubmitting(false);
    }
  };

  const aiAgentCategories = [
    {
      id: 'finance',
      title: 'Finance & Accounting Optimization',
      description: 'Improve liquidity, strengthen controls, and uncover financial inefficiencies.',
      icon: 'ri-line-chart-line',
      services: [
        'Accounts Receivable Acceleration',
        'Bad Debt Recovery Blueprint',
        'Duplicate Payment Recovery',
        'Vendor Overpayment Analysis',
        'Cash Conversion Cycle Optimization',
        'Budget Variance Analysis',
        'Scenario‑based Cash Flow Modeling',
        'Capital Expenditure Review',
        'Profitability By Segment Analysis',
        'Business Valuation Readiness Assessment'
      ]
    },
    {
      id: 'tax',
      title: 'Tax, Compliance & Audit Readiness',
      description: 'Reduce risk, automate compliance, and prepare for regulatory scrutiny.',
      icon: 'ri-file-shield-line',
      services: [
        'Automated Tax Preparation Workflow',
        'IRS Audit Readiness Package',
        'PPP Forgiveness Compliance Review',
        'Aggregation Rule Compliance Check',
        'Wage & Hour Law Compliance Audit',
        'Employee Classification Risk Review',
        'Data Privacy Compliance Audit',
        'AI‑driven Compliance Monitoring',
        'Document Retention Policy Builder',
        'HR Compliance Workflow Builder'
      ]
    },
    {
      id: 'payroll',
      title: 'Payroll, HRIS & Workforce Automation',
      description: 'Streamline payroll, reduce errors, and strengthen workforce compliance.',
      icon: 'ri-team-line',
      services: [
        'Payroll Process Automation Blueprint',
        'Payroll System Integration Blueprint',
        'Timekeeping System Audit',
        'Digital Onboarding Process Design',
        'Employee Turnover Analysis',
        'Workforce Diversity Analytics',
        'Overtime Cost Reduction Analysis'
      ]
    },
    {
      id: 'operations',
      title: 'Operational Efficiency & Process Automation',
      description: 'Eliminate manual work, modernize workflows, and improve cross‑department efficiency.',
      icon: 'ri-settings-3-line',
      services: [
        'Digital Expense Reporting Implementation',
        'E‑invoicing Solution Design',
        'BI Dashboard Development',
        'Business Continuity Plan Development',
        'Supply Chain Resilience Audit',
        'Inventory Optimization Blueprint'
      ]
    },
    {
      id: 'realestate',
      title: 'Real Estate & Facilities Optimization',
      description: 'Reduce occupancy costs and improve portfolio performance.',
      icon: 'ri-building-line',
      services: [
        'Real Estate Lease Optimization',
        'Commercial Lease Expense Review'
      ]
    },
    {
      id: 'telecom',
      title: 'Telecom, Utilities & Vendor Spend Recovery',
      description: 'Identify overcharges, recover costs, and strengthen vendor oversight.',
      icon: 'ri-phone-line',
      services: [
        'Telecom & Utility Bill Audit',
        'Insurance Premium Audit',
        'Freight & Shipping Cost Optimization'
      ]
    },
    {
      id: 'revenue',
      title: 'Revenue Assurance & Leakage Prevention',
      description: 'Protect top‑line revenue with forensic analysis and compliance controls.',
      icon: 'ri-money-dollar-circle-line',
      services: [
        'Channel Partner Revenue Audit',
        'Revenue Leakage Detection',
        'Franchise Fee Recovery'
      ]
    },
    {
      id: 'nonprofit',
      title: 'Nonprofit & Grant Management',
      description: 'Strengthen compliance, reporting, and grant stewardship.',
      icon: 'ri-hand-heart-line',
      services: [
        'Nonprofit Grant Compliance Toolkit'
      ]
    },
    {
      id: 'leadership',
      title: 'Leadership, Strategy & Organizational Planning',
      description: 'Build resilient leadership pipelines and strategic readiness.',
      icon: 'ri-lightbulb-line',
      services: [
        'Succession Planning Toolkit',
        'Training ROI Calculator',
        'Business Continuity Plan Development'
      ]
    },
    {
      id: 'risk',
      title: 'Risk Management & Regulatory Controls',
      description: 'Mitigate exposure and build defensible compliance frameworks.',
      icon: 'ri-shield-check-line',
      services: [
        'Data Privacy Compliance Audit',
        'Employee Classification Risk Review',
        'Wage & Hour Law Compliance Audit',
        'AI‑driven Compliance Monitoring',
        'Document Retention Policy Builder'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-xl border-b border-gray-200 shadow-sm' : 'bg-white/80 backdrop-blur-md'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <a href="/" className="flex items-center hover:opacity-90 transition-opacity">
              <img 
                alt="InstaVisible Tax & Savings Solutions Logo" 
                className="h-14 w-auto object-contain" 
                src="https://static.readdy.ai/image/4a891bf01ca77aa59d4e684a3f5f885b/e200ded0512fa4f1c42cf2a76268a351.webp"
              />
            </a>
            
            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center gap-1">
              <button onClick={() => scrollToSection('home')} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 whitespace-nowrap">Home</button>
              <button onClick={() => scrollToSection('services')} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 whitespace-nowrap">Services</button>
              <button onClick={() => scrollToSection('ai-agents')} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 whitespace-nowrap">AI Agents</button>
              <button onClick={() => navigate('/ai-agents')} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 whitespace-nowrap">All AI Services</button>
              <button onClick={() => navigate('/blog')} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 whitespace-nowrap">Blog</button>
              <button onClick={() => scrollToSection('contact')} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 whitespace-nowrap">Contact</button>
              <div className="w-px h-6 bg-gray-300 mx-2"></div>
              <button onClick={() => setShowDemoModal(true)} className="inline-flex items-center cursor-pointer justify-center gap-2 whitespace-nowrap rounded-lg text-sm transition-all h-9 px-4 py-2 bg-emerald-600 text-white hover:bg-emerald-700 font-medium ml-2">Request a Video Demo</button>
            </div>

            {/* Mobile Menu Button */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl text-gray-700`}></i>
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden py-4 border-t border-gray-200">
              <div className="flex flex-col gap-2">
                <button onClick={() => scrollToSection('home')} className="px-4 py-3 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 text-left whitespace-nowrap">Home</button>
                <button onClick={() => scrollToSection('services')} className="px-4 py-3 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 text-left whitespace-nowrap">Services</button>
                <button onClick={() => scrollToSection('ai-agents')} className="px-4 py-3 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 text-left whitespace-nowrap">AI Agents</button>
                <button onClick={() => navigate('/ai-agents')} className="px-4 py-3 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 text-left whitespace-nowrap">All AI Services</button>
                <button onClick={() => navigate('/blog')} className="px-4 py-3 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 text-left whitespace-nowrap">Blog</button>
                <button onClick={() => scrollToSection('contact')} className="px-4 py-3 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 text-left whitespace-nowrap">Contact</button>
                <button onClick={() => { setShowDemoModal(true); setMobileMenuOpen(false); }} className="mx-4 mt-2 inline-flex items-center cursor-pointer justify-center gap-2 whitespace-nowrap rounded-lg text-sm transition-all h-11 px-4 py-2 bg-emerald-600 text-white hover:bg-emerald-700 font-medium">Request a Video Demo</button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section - Modern Split Design */}
      <section id="home" className="relative min-h-screen flex items-center px-6 lg:px-8 pt-20 bg-gradient-to-br from-emerald-50 via-white to-teal-50">
        <div className="max-w-7xl mx-auto w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-emerald-100 border border-emerald-200">
                <div className="relative">
                  <div className="w-2 h-2 bg-emerald-600 rounded-full animate-pulse"></div>
                  <div className="absolute inset-0 w-2 h-2 bg-emerald-600 rounded-full animate-ping opacity-75"></div>
                </div>
                <span className="text-sm text-emerald-900 font-medium">AI-Powered Tax Intelligence</span>
              </div>
              
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 leading-tight tracking-tight">
                <strong>AI Tax Software</strong> for<br />
                <span className="bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-700 bg-clip-text text-transparent">Automated Tax Preparation</span>
              </h1>
              
              <p className="text-xl text-gray-600 leading-relaxed">
                <strong>Professional tax software</strong> with <strong>automated tax preparation</strong>, <strong>1040 amendments</strong>, and <strong>multi-state tax projections</strong> across all 50 states. Powered by 45+ specialized <strong>AI tax assistants</strong> trained to optimize your <strong>tax practice management</strong> and uncover hidden savings with intelligent <strong>tax automation software</strong>.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <button onClick={() => setShowDemoModal(true)} className="inline-flex items-center cursor-pointer justify-center gap-2 whitespace-nowrap bg-emerald-600 text-white hover:bg-emerald-700 text-base font-semibold px-8 h-14 rounded-xl shadow-lg shadow-emerald-600/30 hover:shadow-emerald-700/40 transition-all duration-300 hover:scale-105">
                  Request a Video Demo
                  <i className="ri-arrow-right-line text-lg"></i>
                </button>
                <button onClick={() => setShowEnquiryModal(true)} className="inline-flex items-center cursor-pointer justify-center gap-2 whitespace-nowrap text-base font-semibold px-8 h-14 rounded-xl border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50 transition-all duration-300">
                  Send Inquiry
                </button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-6 pt-8">
                <div>
                  <div className="text-3xl font-bold text-emerald-600">50</div>
                  <div className="text-sm text-gray-600">States Tax Coverage</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-emerald-600">45+</div>
                  <div className="text-sm text-gray-600">AI Tax Experts</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-emerald-600">30+</div>
                  <div className="text-sm text-gray-600">Automated Tax Forms</div>
                </div>
              </div>
            </div>

            {/* Right Visual */}
            <div className="relative hidden lg:block">
              <div className="relative w-full h-[600px] rounded-3xl overflow-hidden bg-gradient-to-br from-emerald-100 to-teal-100 p-8">
                <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(16,185,129,0.1)_1px,transparent_1px),linear-gradient(to_bottom,rgba(16,185,129,0.1)_1px,transparent_1px)] bg-[size:3rem_3rem]"></div>
                <div className="relative z-10 space-y-6">
                  <div className="bg-white rounded-2xl p-6 shadow-xl transform hover:scale-105 transition-transform duration-300">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                        <i className="ri-file-list-3-line text-2xl text-emerald-600"></i>
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">30+ Tax Forms</div>
                        <div className="text-sm text-gray-500">Federal & State Coverage</div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <div className="px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-medium">1040</div>
                      <div className="px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-medium">1065</div>
                      <div className="px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-medium">1120</div>
                      <div className="px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-medium">1099</div>
                      <div className="px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-medium">5500</div>
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl p-6 shadow-xl transform hover:scale-105 transition-transform duration-300 ml-8">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                        <i className="ri-robot-line text-2xl text-teal-600"></i>
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">AI Tax Companion</div>
                        <div className="text-sm text-gray-500">24/7 Expert Guidance</div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <div className="px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-medium">Active</div>
                      <div className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">45 Experts</div>
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl p-6 shadow-xl transform hover:scale-105 transition-transform duration-300">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                        <i className="ri-map-pin-line text-2xl text-emerald-600"></i>
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">State Projections</div>
                        <div className="text-sm text-gray-500">All 50 States</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-5 gap-2">
                      {['CA', 'NY', 'TX', 'FL', 'IL'].map((state) => (
                        <div key={state} className="px-2 py-1 bg-emerald-50 text-emerald-700 rounded text-xs font-medium text-center">{state}</div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - Tax Forms */}
      <section id="services" className="py-24 px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-6 tracking-tight"><strong>Professional Tax Software</strong> with Comprehensive Form Support</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              <strong>AI-driven tax automation</strong> for data entry and error checking across all major tax forms, as well as amendment finding &amp; form 1040X completion. Streamline <strong>tax compliance automation</strong> with intelligent <strong>automated tax workflows</strong> designed for <strong>CPA tax software</strong> and <strong>tax preparer tools</strong>.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Individual Income Tax Forms */}
            <div className="group p-8 bg-gradient-to-br from-emerald-50 to-white border-2 border-gray-200 hover:border-emerald-400 transition-all duration-500 rounded-2xl hover:shadow-xl">
              <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-all duration-300">
                <i className="ri-file-text-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Individual Income Tax Forms</h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-600 mt-1 flex-shrink-0"></i>
                  <span><strong>Form 1040</strong> &amp; variants (1040-SR for seniors)</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-600 mt-1 flex-shrink-0"></i>
                  <span><strong>1040X preparation</strong> - Automated <strong>tax amendment finding and form 1040X drafting</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-600 mt-1 flex-shrink-0"></i>
                  <span>Schedule A (Itemized Deductions)</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-600 mt-1 flex-shrink-0"></i>
                  <span>Schedule C (Business Profit/Loss)</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-600 mt-1 flex-shrink-0"></i>
                  <span>Schedule D (Capital Gains/Losses)</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-600 mt-1 flex-shrink-0"></i>
                  <span>Schedule E (Supplemental Income)</span>
                </li>
              </ul>
            </div>

            {/* Business & Self-Employment Forms */}
            <div className="group p-8 bg-gradient-to-br from-teal-50 to-white border-2 border-gray-200 hover:border-emerald-400 transition-all duration-500 rounded-2xl hover:shadow-xl">
              <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-all duration-300">
                <i className="ri-briefcase-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Business & Self-Employment</h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-600 mt-1 flex-shrink-0"></i>
                  <span>Form 1099 series (1099-NEC, 1099-MISC)</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-600 mt-1 flex-shrink-0"></i>
                  <span>Form 1065 (Partnership Income) & Schedule K-1</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-600 mt-1 flex-shrink-0"></i>
                  <span>Form 1120 (C-corp Income Tax)</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-600 mt-1 flex-shrink-0"></i>
                  <span>Form 1120-S (S-corp Income Tax)</span>
                </li>
              </ul>
            </div>

            {/* Specialized Forms */}
            <div className="group p-8 bg-gradient-to-br from-emerald-50 to-white border-2 border-gray-200 hover:border-emerald-400 transition-all duration-500 rounded-2xl hover:shadow-xl">
              <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-all duration-300">
                <i className="ri-file-shield-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Specialized Forms</h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-600 mt-1 flex-shrink-0"></i>
                  <span>Form 5500 (Employee Benefit Plan)</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-600 mt-1 flex-shrink-0"></i>
                  <span>Form 1098 (Mortgage Interest)</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-600 mt-1 flex-shrink-0"></i>
                  <span>Form W-2 (Wage &amp; Tax Statement)</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-600 mt-1 flex-shrink-0"></i>
                  <span>Form 1040X (Amended returns)</span>
                </li>
              </ul>
            </div>

            {/* State Tax Forms */}
            <div className="group p-8 bg-gradient-to-br from-teal-50 to-white border-2 border-gray-200 hover:border-emerald-400 transition-all duration-500 rounded-2xl hover:shadow-xl md:col-span-2 lg:col-span-1">
              <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-all duration-300">
                <i className="ri-map-pin-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">State Tax Forms</h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-600 mt-1 flex-shrink-0"></i>
                  <span>All 50 states supported</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-600 mt-1 flex-shrink-0"></i>
                  <span>California Form 540</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-600 mt-1 flex-shrink-0"></i>
                  <span>New York IT-201</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-600 mt-1 flex-shrink-0"></i>
                  <span>Integrated with federal returns</span>
                </li>
              </ul>
            </div>

            {/* Automation Features */}
            <div className="group p-8 bg-gradient-to-br from-emerald-600 to-teal-700 text-white border-2 border-emerald-600 transition-all duration-500 rounded-2xl hover:shadow-xl md:col-span-2">
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-all duration-300">
                <i className="ri-robot-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4"><strong>AI-Powered Tax Automation</strong></h3>
              <p className="text-white/90 mb-6">
                InstaVisible uses advanced <strong>AI tax software</strong> for <strong>automated tax preparation</strong>, intelligent error checking, and seamless e-filing. Our <strong>amendment finding software</strong> and <strong>tax credits automation</strong> help you discover opportunities while reducing <strong>tax preparation time</strong>. Access all features via the Tax Preparation and E-Filing dashboard sections.
              </p>
              <div className="flex flex-wrap gap-3">
                <div className="px-4 py-2 bg-white/20 rounded-lg text-sm font-medium">Automated Data Entry</div>
                <div className="px-4 py-2 bg-white/20 rounded-lg text-sm font-medium">Amendment Finding</div>
                <div className="px-4 py-2 bg-white/20 rounded-lg text-sm font-medium">Tax Credits Discovery</div>
                <div className="px-4 py-2 bg-white/20 rounded-lg text-sm font-medium">Multi-State Preparation</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Agents Section */}
      <section id="ai-agents" className="py-24 px-6 lg:px-8 bg-gradient-to-b from-white via-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-emerald-100 border border-emerald-200 mb-6">
              <i className="ri-robot-line text-emerald-600"></i>
              <span className="text-sm text-emerald-900 font-medium">45+ Specialized <strong>AI Tax Assistants</strong></span>
            </div>
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-6 tracking-tight"><strong>AI Tax Assistant</strong> Services for <strong>Tax Practice Management</strong></h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Trained on specific industries and business aspects, our <strong>AI tax companion</strong> is ready to answer your specialized questions 24/7, helping you find additional savings, optimize <strong>tax workflow automation</strong>, and improve <strong>tax efficiency software</strong> performance across your entire practice.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {aiAgentCategories.map((category) => (
              <div key={category.id} className="bg-white border-2 border-gray-200 rounded-2xl overflow-hidden hover:border-emerald-400 transition-all duration-300">
                <button
                  onClick={() => setExpandedCategory(expandedCategory === category.id ? null : category.id)}
                  className="w-full p-6 flex items-start gap-4 text-left cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className={`${category.icon} text-xl text-white`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{category.title}</h3>
                    <p className="text-sm text-gray-600">{category.description}</p>
                  </div>
                  <i className={`ri-arrow-down-s-line text-2xl text-gray-400 transition-transform duration-300 flex-shrink-0 ${expandedCategory === category.id ? 'rotate-180' : ''}`}></i>
                </button>
                
                {expandedCategory === category.id && (
                  <div className="px-6 pb-6 border-t border-gray-100">
                    <ul className="space-y-3 pt-4">
                      {category.services.map((service, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-gray-700">
                          <i className="ri-check-line text-emerald-600 mt-0.5 flex-shrink-0"></i>
                          <span className="text-sm">{service}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <button onClick={() => navigate('/ai-agents')} className="inline-flex items-center cursor-pointer justify-center gap-2 whitespace-nowrap bg-emerald-600 text-white hover:bg-emerald-700 text-base font-semibold px-8 h-14 rounded-xl shadow-lg shadow-emerald-600/30 hover:shadow-emerald-700/40 transition-all duration-300 hover:scale-105">
              View All AI Services
              <i className="ri-arrow-right-line text-lg"></i>
            </button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="py-24 px-6 lg:px-8 bg-gradient-to-br from-emerald-600 to-teal-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.1)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6 tracking-tight">
            Ready to Transform Your <strong>Tax Practice</strong>?
          </h2>
          <p className="text-xl text-white/90 mb-10 leading-relaxed">
            Join <strong>tax professionals</strong>, <strong>CPAs</strong>, and <strong>enrolled agents</strong> who are already saving hours every day with <strong>AI-powered tax intelligence</strong> and <strong>automated tax workflows</strong>
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button onClick={() => setShowDemoModal(true)} className="inline-flex items-center cursor-pointer justify-center gap-2 whitespace-nowrap bg-white text-emerald-700 hover:bg-gray-50 text-base font-semibold px-8 h-14 rounded-xl shadow-lg transition-all duration-300 hover:scale-105">
              Request a Video Demo
              <i className="ri-arrow-right-line text-lg"></i>
            </button>
            <button onClick={() => setShowEnquiryModal(true)} className="inline-flex items-center cursor-pointer justify-center gap-2 whitespace-nowrap border-2 border-white text-white hover:bg-white/10 text-base font-semibold px-8 h-14 rounded-xl transition-all duration-300">
              Send Inquiry
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-emerald-600">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center mb-6">
                <img 
                  alt="InstaVisible Tax & Savings Solutions - AI Tax Software for Automated Tax Filing" 
                  className="h-14 w-auto object-contain brightness-0 invert" 
                  src="https://static.readdy.ai/image/4a891bf01ca77aa59d4e684a3f5f885b/e200ded0512fa4f1c42cf2a76268a351.webp"
                />
              </div>
              <p className="text-white/90 leading-relaxed max-w-md">
                <strong>Professional AI tax software</strong> for CPAs, tax professionals and CFOs. Streamline your <strong>tax workflow automation</strong> with cutting-edge <strong>automated tax preparation</strong> technology, <strong>1040 amendments</strong>, and <strong>multi-state tax projections</strong>.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-white mb-6">Product</h3>
              <ul className="space-y-4">
                <li><button onClick={() => scrollToSection('services')} className="text-white/80 hover:text-white transition-colors whitespace-nowrap">Services</button></li>
                <li><button onClick={() => scrollToSection('ai-agents')} className="text-white/80 hover:text-white transition-colors whitespace-nowrap">AI Agents</button></li>
                <li><button onClick={() => navigate('/ai-agents')} className="text-white/80 hover:text-white transition-colors whitespace-nowrap cursor-pointer">All AI Services</button></li>
                <li><a href="https://itsjusttax.com" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white transition-colors whitespace-nowrap">Blog</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold text-white mb-6">Company</h3>
              <ul className="space-y-4">
                <li><button onClick={() => setShowEnquiryModal(true)} className="text-white/80 hover:text-white transition-colors whitespace-nowrap cursor-pointer">Contact</button></li>
                <li><button onClick={() => navigate('/partnerships')} className="text-white/80 hover:text-white transition-colors whitespace-nowrap cursor-pointer">Partnerships</button></li>
              </ul>
            </div>
          </div>
          
          <div className="pt-8 border-t border-white/20 flex flex-col sm:flex-row items-center justify-between gap-6">
            <p className="text-white/80">© 2025 InstaVisible Tax & Savings Solutions. All rights reserved.</p>
            <div className="flex items-center gap-6 sm:gap-8 text-white/80 flex-wrap justify-center">
              <a href="https://my-web-1763232775898.estage.site/privacy-policy" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors whitespace-nowrap">Privacy Policy</a>
              <a href="https://my-web-1763232775898.estage.site/terms-of-use" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors whitespace-nowrap">Terms of Use</a>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors whitespace-nowrap">Powered by Readdy</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Video Demo Modal */}
      {showDemoModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={() => setShowDemoModal(false)}>
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-8 md:p-12">
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-3xl md:text-4xl font-bold text-gray-900">Request a Video Demo</h3>
                <button onClick={() => setShowDemoModal(false)} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer">
                  <i className="ri-close-line text-2xl text-gray-600"></i>
                </button>
              </div>
              
              {demoSuccess ? (
                <div className="py-12 text-center">
                  <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-check-line text-4xl text-emerald-600"></i>
                  </div>
                  <h4 className="text-2xl font-bold text-gray-900 mb-3">Request Submitted!</h4>
                  <p className="text-gray-600 text-lg">Redirecting to demo video...</p>
                </div>
              ) : (
                <form id="demo-form" data-readdy-form onSubmit={handleDemoSubmit} className="space-y-6">
                  {demoError && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                      <i className="ri-error-warning-line text-red-600 text-xl flex-shrink-0 mt-0.5"></i>
                      <p className="text-sm text-red-800">{demoError}</p>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={demoFormData.name}
                      onChange={(e) => setDemoFormData({...demoFormData, name: e.target.value})}
                      required
                      className="w-full px-4 h-12 border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:outline-none transition-colors text-sm"
                      placeholder="John Smith"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={demoFormData.email}
                      onChange={(e) => setDemoFormData({...demoFormData, email: e.target.value})}
                      required
                      className="w-full px-4 h-12 border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:outline-none transition-colors text-sm"
                      placeholder="john@company.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={demoFormData.phone}
                      onChange={(e) => setDemoFormData({...demoFormData, phone: e.target.value})}
                      required
                      className="w-full px-4 h-12 border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:outline-none transition-colors text-sm"
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Additional Information</label>
                    <textarea
                      name="message"
                      value={demoFormData.message}
                      onChange={(e) => setDemoFormData({...demoFormData, message: e.target.value})}
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:outline-none transition-colors resize-none text-sm"
                      placeholder="Tell us about your needs or any specific features you'd like to see..."
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-2">Maximum 500 characters</p>
                  </div>

                  <button
                    type="submit"
                    disabled={demoSubmitting}
                    className="w-full bg-emerald-600 text-white hover:bg-emerald-700 h-14 rounded-2xl font-semibold text-base transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {demoSubmitting ? 'Submitting...' : 'Submit Request'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Enquiry Modal */}
      {showEnquiryModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={() => setShowEnquiryModal(false)}>
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-8 md:p-12">
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-3xl md:text-4xl font-bold text-gray-900">Send an Inquiry</h3>
                <button onClick={() => setShowEnquiryModal(false)} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer">
                  <i className="ri-close-line text-2xl text-gray-600"></i>
                </button>
              </div>
              
              {enquirySuccess ? (
                <div className="py-12 text-center">
                  <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-check-line text-4xl text-emerald-600"></i>
                  </div>
                  <h4 className="text-2xl font-bold text-gray-900 mb-3">Inquiry Sent!</h4>
                  <p className="text-gray-600 text-lg">Redirecting to demo video...</p>
                </div>
              ) : (
                <form id="enquiry-form" data-readdy-form onSubmit={handleEnquirySubmit} className="space-y-6">
                  {enquiryError && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                      <i className="ri-error-warning-line text-red-600 text-xl flex-shrink-0 mt-0.5"></i>
                      <p className="text-sm text-red-800">{enquiryError}</p>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={enquiryFormData.name}
                      onChange={(e) => setEnquiryFormData({...enquiryFormData, name: e.target.value})}
                      required
                      className="w-full px-4 h-12 border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:outline-none transition-colors text-sm"
                      placeholder="John Smith"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={enquiryFormData.email}
                      onChange={(e) => setEnquiryFormData({...enquiryFormData, email: e.target.value})}
                      required
                      className="w-full px-4 h-12 border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:outline-none transition-colors text-sm"
                      placeholder="john@company.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={enquiryFormData.phone}
                      onChange={(e) => setEnquiryFormData({...enquiryFormData, phone: e.target.value})}
                      required
                      className="w-full px-4 h-12 border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:outline-none transition-colors text-sm"
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Subject *</label>
                    <input
                      type="text"
                      name="subject"
                      value={enquiryFormData.subject}
                      onChange={(e) => setEnquiryFormData({...enquiryFormData, subject: e.target.value})}
                      required
                      className="w-full px-4 h-12 border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:outline-none transition-colors text-sm"
                      placeholder="What would you like to know?"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Message *</label>
                    <textarea
                      name="message"
                      value={enquiryFormData.message}
                      onChange={(e) => setEnquiryFormData({...enquiryFormData, message: e.target.value})}
                      required
                      maxLength={500}
                      rows={6}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:outline-none transition-colors resize-none text-sm"
                      placeholder="Please provide details about your inquiry..."
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-2">Maximum 500 characters</p>
                  </div>

                  <button
                    type="submit"
                    disabled={enquirySubmitting}
                    className="w-full bg-emerald-600 text-white hover:bg-emerald-700 h-14 rounded-2xl font-semibold text-base transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {enquirySubmitting ? 'Sending...' : 'Send Enquiry'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
